import clsx from "clsx";
import React from "react";
import { NavLink, useLocation } from "react-router-dom";

import ThemeToggleCompact from "~/components/settings/ThemeToggleCompact";
import { useNotificationsMetrics } from "~/hooks/useNotificationsMetrics";
import {
  CURRENT_USER_MENU_TYPE,
  HELP_CENTER_MENU_TYPE,
  NOTIFICATIONS_MENU_TYPE,
  useSidebar,
} from "~/hooks/useSidebar";
import { useTeams } from "~/hooks/useTeams";
import { Logo, getWhiteLabelConfig } from "~/utils/white-label";

import { Icon } from "../../assets/icons/Icon";
import Button from "../ui/button/Button";
import style from "./DashboardSidebar.module.scss";
import Menu from "./Menu";
import { SidebarUserMenu } from "./SidebarUserMenu";
import NotificationsPopup from "./notifications/NotificationsPopup";
import { TeamSelect } from "./team_select/TeamSelect";

type SidebarProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
};

const Sidebar: React.FC<SidebarProps> = ({
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  const { data: teams = [] } = useTeams();
  const { menu, userMenu } = useSidebar();
  const { unreadCount } = useNotificationsMetrics();
  const location = useLocation();
  const activePath = location.pathname;

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const cfg = getWhiteLabelConfig();
  const showLogo = !!cfg.logo_url;

  const helpCenterItem = userMenu.find((i) => i.type === HELP_CENTER_MENU_TYPE);
  const currentUserItem = userMenu.find(
    (i) => i.type === CURRENT_USER_MENU_TYPE,
  );
  const notificationsItem = userMenu.find(
    (i) => i.type === NOTIFICATIONS_MENU_TYPE,
  );

  return (
    <div
      className={clsx(style.sidebar, { [style.sidebarClosed]: !isSidebarOpen })}
    >
      <div className={style.sidebarContent}>
        {showLogo && (
          <div className={isSidebarOpen ? style.logo : style.logoClosed}>
            <Logo />
          </div>
        )}

        {teams.length > 0 && <TeamSelect isSidebarOpen={isSidebarOpen} />}

        <div className={style.mainMenu}>
          <Menu
            items={menu}
            isSidebarOpen={isSidebarOpen}
            activePath={activePath}
          />
        </div>

        <div>
          <ThemeToggleCompact
            isSidebarOpen={isSidebarOpen}
            className={style.menuLink}
          />

          {notificationsItem && (
            <NotificationsPopup
              isSidebarOpen={isSidebarOpen}
              unreadCount={unreadCount}
            />
          )}

          {helpCenterItem && (
            <NavLink
              to={helpCenterItem.link}
              className={clsx(style.menuLink, {
                // @ts-expect-error TS2339 non-existing className
                [style.menuLinkClosed]: !isSidebarOpen,
                [style.menuLinkActive]: activePath.startsWith(
                  helpCenterItem.link,
                ),
              })}
            >
              <span className={style.menuLinkIconTitle}>
                <span className={style.menuIconTitle}>
                  <Icon
                    iconName="HelpCenterIcon"
                    width="1.5rem"
                    height="1.5rem"
                    className={style.fillHover}
                  />
                  {isSidebarOpen && (
                    <span className={style.label}>{helpCenterItem.label}</span>
                  )}
                </span>
              </span>
            </NavLink>
          )}

          {currentUserItem && (
            <SidebarUserMenu
              isSidebarOpen={isSidebarOpen}
              label={currentUserItem.label}
            />
          )}
        </div>

        <div
          className={clsx(style.footer, {
            [style.footerClosed]: !isSidebarOpen,
          })}
        >
          <Button noHover onClick={toggleSidebar}>
            {isSidebarOpen ? (
              <Icon iconName="ArrowLeftIcon" width="1rem" height="1rem" />
            ) : (
              <Icon iconName="ArrowRightIcon" width="1rem" height="1rem" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
